import { forwardRef, useState, useEffect } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { parse, format, setMonth, getMonth, getYear } from "date-fns";

interface MonthPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disable?: boolean;
  name?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  minDate?: string | null;
  maxDate?: string | null;
}

export type Ref = HTMLButtonElement;

export const MonthPicker = forwardRef<Ref, MonthPickerProps>(
  (
    {
      value,
      onChange,
      className,
      disable,
      name,
      onBlur,
      onFocus,
      minDate,
      maxDate,
    },
    ref,
  ) => {
    const parseDate = (dateStr: string) => {
      if (!dateStr) return null;
      try {
        let date = parse(dateStr, "yyyy-MM-dd", new Date());
        if (!isNaN(date.getTime())) return date;

        date = parse(dateStr, "yyyy-MM", new Date());
        if (!isNaN(date.getTime())) return date;

        return null;
      } catch {
        return null;
      }
    };

    const parseMinOrMaxDate = (dateStr: string) => {
      if (!dateStr) return null;
      try {
        const date = parse(dateStr, "yyyy-MM-dd", new Date());
        return isNaN(date.getTime()) ? null : date;
      } catch {
        return null;
      }
    };

    const initialDate = parseDate(value || "");
    const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
    const [viewYear, setViewYear] = useState<number>(
      initialDate ? getYear(initialDate) : getYear(new Date()),
    );

    const minDateObj = parseMinOrMaxDate(minDate || "");
    const maxDateObj = parseMinOrMaxDate(maxDate || "");

    const isMonthDisabled = (month: number, year: number) => {
      const monthDate = setMonth(new Date(year, 0), month);

      if (minDateObj && monthDate < minDateObj) {
        return true;
      }

      if (maxDateObj && monthDate > maxDateObj) {
        return true;
      }

      return false;
    };

    useEffect(() => {
      if (value) {
        const newDate = parseDate(value);
        if (newDate) {
          setSelectedDate(newDate);
          setViewYear(getYear(newDate));
        }
      }
    }, [value]);

    const handleMonthSelect = (month: number) => {
      if (isMonthDisabled(month, viewYear)) {
        return;
      }

      const date = setMonth(new Date(viewYear, 0), month);
      setSelectedDate(date);
      const formattedDate = format(date, "yyyy-MM");
      onChange?.(formattedDate);
    };

    const formatMonthYear = (date: Date | null) =>
      date ? format(date, "MM/yyyy") : "MM/AAAA";

    const months = Array.from({ length: 12 }, (_, i) =>
      format(setMonth(new Date(0), i), "MMM"),
    );

    return (
      <div className="relative">
        <input
          type="hidden"
          name={name}
          value={selectedDate ? format(selectedDate, "yyyy-MM") : ""}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-between disabled:text-black disabled:bg-white disabled:opacity-100",
                className,
              )}
              disabled={disable}
              onBlur={onBlur}
              onFocus={onFocus}
            >
              <span>{formatMonthYear(selectedDate)}</span>
              {!disable && <CalendarIcon className="h-4 w-4 ml-2 text-black" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="flex justify-between items-center px-2 py-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewYear((y) => y - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span>{viewYear}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewYear((y) => y + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2 p-2">
              {months.map((label, idx) => {
                const isDisabled = isMonthDisabled(idx, viewYear);
                return (
                  <Button
                    key={idx}
                    variant={
                      selectedDate &&
                      getMonth(selectedDate) === idx &&
                      getYear(selectedDate) === viewYear
                        ? "default"
                        : "outline"
                    }
                    className={cn(
                      "w-full",
                      isDisabled && "opacity-50 cursor-not-allowed",
                    )}
                    ref={ref}
                    onClick={() => handleMonthSelect(idx)}
                    disabled={isDisabled}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);
MonthPicker.displayName = "MonthPicker";
